// src/utils/mockDataJobEvents.ts
// ─────────────────────────────────────────────────────────────────
// Job Events Log — Phase 2 Mock Implementation
//
// Provides a module-level events store that any platform module
// can write to. Records appear in the Jobs / Project Register
// Events log. At beta: replaced by POST /api/jobs/:id/events.
//
// Phase 2 writes: Recall events (from recallService)
// Phase 4 writes: WHS critical incidents (from whsInboxService)
// Phase 5 writes: Material delivery, milestone events
// ─────────────────────────────────────────────────────────────────

export type JobEventType =
  | 'SITE_RECALL'
  | 'RECALL_RESOLVED'
  | 'WHS_CRITICAL'
  | 'WHS_HIGH'
  | 'SWMS_UPDATED'
  | 'PERMIT_ISSUED'
  | 'MILESTONE_REACHED'
  | 'MATERIAL_DELIVERED'
  | 'PUSH_SENT'
  | 'GENERAL'

export interface JobEvent {
  id:          string
  jobId:       string
  jobNumber:   string
  type:        JobEventType
  title:       string
  description: string
  refId:       string | null   // e.g. recall ID, WHS ref, PO number
  refType:     string | null   // e.g. 'RECALL', 'WHS_SUBMISSION', 'PO'
  createdAt:   string          // ISO timestamp
  createdById: string
  createdByName: string
  severity:    'INFO' | 'WARNING' | 'CRITICAL'
}

// ── Module-level store ─────────────────────────────────────────────
let _events: JobEvent[] = []
let _eventSeq = 0

function nextEventId(): string {
  return `JE-${String(++_eventSeq).padStart(5, '0')}`
}

// ── Write ─────────────────────────────────────────────────────────
export function addJobEvent(
  jobId: string,
  jobNumber: string,
  input: Omit<JobEvent, 'id' | 'jobId' | 'jobNumber' | 'createdAt'> & { createdAt?: string }
): JobEvent {
  const event: JobEvent = {
    ...input,
    id:        nextEventId(),
    jobId,
    jobNumber,
    createdAt: input.createdAt ?? new Date().toISOString(),
  }
  _events.push(event)
  return event
}

// ── Read ──────────────────────────────────────────────────────────
export function getJobEvents(jobId?: string): JobEvent[] {
  const results = jobId ? _events.filter(e => e.jobId === jobId) : [..._events]
  return results.slice().sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
}

export function getJobEventsByRef(refId: string): JobEvent[] {
  return _events.filter(e => e.refId === refId)
}

// ── Reset (for testing) ────────────────────────────────────────────
export function _resetJobEvents(): void {
  _events = []
  _eventSeq = 0
}
